import { useState, useEffect, useCallback } from 'react';
import meetingsDataService from '../services/meetingsDataService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook pour gérer les meetings avec leurs transcriptions et résumés
 * Utilise le service unifié avec onSnapshot pour le temps réel
 */
export const useMeetingsWithData = () => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const { currentUser } = useAuth();

  // Fonction pour initialiser le service
  const initializeService = useCallback(async () => {
    if (!currentUser) {
      setError('Utilisateur non authentifié');
      setLoading(false);
      return;
    }

    try {
      setError(null);
      setLoading(true);
      
      await meetingsDataService.initialize();
      setIsInitialized(true);
      
    } catch (err) {

      setError(err.message);
      setLoading(false);
    }
  }, [currentUser]);

  // Fonction pour gérer les mises à jour des données
  const handleDataUpdate = useCallback((enrichedMeetings) => {
    setMeetings(enrichedMeetings);
    setLoading(false);
    setError(null);
  }, []);

  // Fonction pour gérer les erreurs de connexion
  const handleConnectionError = useCallback((err) => {

    setError('Erreur de connexion. Vérifiez votre connexion internet.');
    // Ne pas mettre loading à false pour permettre la reconnexion
  }, []);

  // Effet principal pour gérer l'authentification et démarrer les listeners
  useEffect(() => {
    let cleanup = null;

    const setupListeners = async () => {
      if (!currentUser || !isInitialized) return;

      try {
        // Démarrer l'écoute complète des données
        cleanup = await meetingsDataService.startCompleteDataListener(handleDataUpdate);
        
      } catch (err) {
        handleConnectionError(err);
      }
    };

    if (isInitialized) {
      setupListeners();
    }

    // Nettoyage lors du démontage
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [currentUser, isInitialized, handleDataUpdate, handleConnectionError]);

  // Effet pour initialiser le service quand l'utilisateur change
  useEffect(() => {
    if (currentUser) {
      initializeService();
    } else {
      // Nettoyage si plus d'utilisateur
      setMeetings([]);
      setLoading(false);
      setError('Utilisateur non authentifié');
      setIsInitialized(false);
      meetingsDataService.cleanup();
    }
  }, [currentUser, initializeService]);

  // Fonction pour récupérer un meeting spécifique avec ses données
  const getMeetingWithData = useCallback((meetingId) => {
    return meetingsDataService.getMeetingWithData(meetingId);
  }, []);

  // Fonction pour rafraîchir le statut admin
  const refreshAdminStatus = useCallback(async () => {
    try {
      const hasChanged = await meetingsDataService.refreshAdminStatus();
      if (hasChanged) {
        // Redémarrer les listeners avec les nouveaux droits
        meetingsDataService.stopAllListeners();
        const cleanup = await meetingsDataService.startCompleteDataListener(handleDataUpdate);
        return cleanup;
      }
    } catch (err) {

    }
  }, [handleDataUpdate]);

  // Fonction pour forcer un rechargement
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      meetingsDataService.stopAllListeners();
      await meetingsDataService.initialize();
      const cleanup = await meetingsDataService.startCompleteDataListener(handleDataUpdate);
      return cleanup;
    } catch (err) {
      handleConnectionError(err);
    }
  }, [handleDataUpdate, handleConnectionError]);

  // Statistiques dérivées
  const stats = {
    totalMeetings: meetings.length,
    meetingsWithTranscription: meetings.filter(m => m.hasTranscription).length,
    meetingsWithSummary: meetings.filter(m => m.hasSummary).length,
    pendingTranscriptions: meetings.filter(m => 
      m.transcription && ['pending', 'processing'].includes(m.transcription.status)
    ).length,
    pendingSummaries: meetings.filter(m => 
      m.summary && m.summary.status === 'En cours'
    ).length
  };

  return {
    // Données principales
    meetings,
    loading,
    error,
    
    // Statistiques
    stats,
    
    // Fonctions utilitaires
    getMeetingWithData,
    refreshAdminStatus,
    reload,
    
    // État du service
    isInitialized,
    isAdmin: meetingsDataService.cache?.userIsAdmin || false
  };
};

export default useMeetingsWithData; 